import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { serviceDeliveryAPI } from '../lib/api';
import { OPT_TIPOS, OPT_CONCLUSIONES } from '../lib/optimizations';
import { Plus, Loader2, Trash2, FlaskConical, ArrowRight, RefreshCw, X } from 'lucide-react';

const tipoLabel = (v) => OPT_TIPOS.find((t) => t.v === v)?.label || v;
const eur = (v) => (v === null || v === undefined) ? '—' : `${Number(v).toLocaleString('es-ES', { maximumFractionDigits: 2 })} €`;
const fmtD = (iso) => iso ? new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

function Snapshot({ title, d }) {
  return (
    <div className="rounded-md border p-2 bg-background">
      <p className="text-[10px] uppercase font-semibold text-muted-foreground">{title}</p>
      {!d ? <p className="text-xs text-muted-foreground italic mt-1">Sin datos</p> : (
        <div className="grid grid-cols-4 gap-1 mt-1 text-xs">
          <div><p className="text-[10px] text-muted-foreground">CPL</p><p className="font-semibold tabular-nums">{d.cpl != null ? eur(d.cpl) : '—'}</p></div>
          <div><p className="text-[10px] text-muted-foreground">Inversión</p><p className="font-semibold tabular-nums">{eur(d.spend)}</p></div>
          <div><p className="text-[10px] text-muted-foreground">CTR</p><p className="font-semibold tabular-nums">{d.ctr != null ? `${d.ctr}%` : '—'}</p></div>
          <div><p className="text-[10px] text-muted-foreground">Leads</p><p className="font-semibold tabular-nums">{d.leads ?? '—'}</p></div>
        </div>
      )}
    </div>
  );
}

function Entry({ clientId, entry, onChanged }) {
  const [busy, setBusy] = useState(false);
  const conc = OPT_CONCLUSIONES.find((c) => c.v === entry.conclusion);
  const cplA = entry.datos_antes?.cpl;
  const cplD = entry.datos_despues?.cpl;
  const cplColor = (cplA != null && cplD != null) ? (cplD < cplA ? 'text-emerald-600' : cplD > cplA ? 'text-red-600' : 'text-muted-foreground') : 'text-muted-foreground';

  const setConclusion = async (v) => {
    setBusy(true);
    try { await serviceDeliveryAPI.updateCampaignOptimization(clientId, entry.id, { conclusion: v }); onChanged(); }
    catch (e) { toast.error(e?.message || 'Error'); }
    finally { setBusy(false); }
  };
  const captureAfter = async () => {
    setBusy(true);
    try { await serviceDeliveryAPI.captureOptimizationAfter(clientId, entry.id); toast.success('Datos de después capturados de Meta'); onChanged(); }
    catch (e) { toast.error(e?.message || 'Error capturando de Meta'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm('¿Eliminar esta optimización del registro?')) return;
    try { await serviceDeliveryAPI.deleteCampaignOptimization(clientId, entry.id); toast.success('Eliminada'); onChanged(); }
    catch (e) { toast.error(e?.message || 'Error'); }
  };

  return (
    <div className="rounded-lg border p-3 space-y-2 bg-muted/10" data-testid={`opt-entry-${entry.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap text-sm">
          <span className="text-xs text-muted-foreground">{fmtD(entry.fecha)}</span>
          <span className="text-[11px] px-2 py-0.5 rounded-full border bg-background font-medium">{tipoLabel(entry.tipo)}</span>
          {cplA != null && (
            <span className={`text-xs inline-flex items-center gap-1 font-medium ${cplColor}`} data-testid={`opt-cpl-${entry.id}`}>
              CPL {eur(cplA)} <ArrowRight className="w-3 h-3" /> {cplD != null ? eur(cplD) : '…'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <select
            value={entry.conclusion || ''}
            onChange={(e) => setConclusion(e.target.value)}
            disabled={busy}
            className={`h-7 rounded-md border px-1.5 text-xs font-medium ${conc ? conc.cls : 'bg-background'}`}
            data-testid={`opt-conclusion-${entry.id}`}
          >
            <option value="">¿Conclusión?</option>
            {OPT_CONCLUSIONES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
          </select>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={remove} data-testid={`opt-delete-${entry.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
      {entry.que_cambie && <p className="text-sm"><span className="text-xs font-semibold text-muted-foreground">Qué cambié: </span>{entry.que_cambie}</p>}
      {entry.por_que && <p className="text-sm"><span className="text-xs font-semibold text-muted-foreground">Por qué: </span>{entry.por_que}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Snapshot title={`Antes (${fmtD(entry.datos_antes?.captured_at) || 'al crear'})`} d={entry.datos_antes} />
        {entry.datos_despues ? (
          <Snapshot title={`Después (${fmtD(entry.datos_despues?.captured_at)}${entry.datos_despues?.auto ? ' · auto' : ''})`} d={entry.datos_despues} />
        ) : (
          <div className="rounded-md border border-dashed p-2 flex flex-col items-start justify-center gap-1">
            <p className="text-[11px] text-muted-foreground">Datos de después: se capturarán solos el {fmtD(entry.after_due_at)}.</p>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={captureAfter} disabled={busy} data-testid={`opt-capture-after-${entry.id}`}>
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />} Capturar ahora
            </Button>
          </div>
        )}
      </div>
      {entry.notas && <p className="text-xs text-muted-foreground whitespace-pre-wrap"><span className="font-semibold">Notas: </span>{entry.notas}</p>}
    </div>
  );
}

export default function CampaignOptimizationsLog({ clientId, campaign, entries, onChanged }) {
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ tipo: 'subir_presupuesto', que_cambie: '', por_que: '', notas: '' });

  const submit = async () => {
    setSaving(true);
    try {
      await serviceDeliveryAPI.createCampaignOptimization(clientId, {
        campaign_id: String(campaign.id), campaign_name: campaign.name, ...form,
      });
      toast.success('Optimización registrada · datos de antes capturados de Meta');
      setForm({ tipo: 'subir_presupuesto', que_cambie: '', por_que: '', notas: '' });
      setFormOpen(false);
      onChanged();
    } catch (e) { toast.error(e?.message || 'Error registrando la optimización'); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg border p-3 bg-muted/20 space-y-2" data-testid={`opt-log-${campaign.id}`}>
      <div className="flex items-center justify-between">
        <Label className="text-xs flex items-center gap-1"><FlaskConical className="w-3.5 h-3.5 text-primary" /> Registro de optimizaciones (interno)</Label>
        <Button variant="outline" size="sm" onClick={() => setFormOpen((v) => !v)} data-testid={`opt-add-btn-${campaign.id}`}>
          {formOpen ? <X className="w-3.5 h-3.5 mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
          {formOpen ? 'Cancelar' : 'Añadir optimización'}
        </Button>
      </div>

      {formOpen && (
        <div className="rounded-lg border bg-background p-3 space-y-2" data-testid={`opt-form-${campaign.id}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Qué cambié</Label>
              <div className="flex gap-2 mt-1">
                <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} className="h-9 rounded-md border px-2 text-sm shrink-0" data-testid={`opt-form-tipo-${campaign.id}`}>
                  {OPT_TIPOS.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                </select>
                <Input value={form.que_cambie} onChange={(e) => setForm({ ...form, que_cambie: e.target.value })} placeholder="Detalle: ej. de 10 €/día a 15 €/día" data-testid={`opt-form-que-${campaign.id}`} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Por qué</Label>
              <Input className="mt-1" value={form.por_que} onChange={(e) => setForm({ ...form, por_que: e.target.value })} placeholder="Ej: el CPL estaba subiendo, el anuncio se quemó..." data-testid={`opt-form-porque-${campaign.id}`} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Notas (opcional)</Label>
            <Textarea className="mt-1 resize-none" rows={2} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
          </div>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-[11px] text-muted-foreground">Al registrar, se capturan automáticamente de Meta el CPL, inversión acumulada, CTR y leads de este momento. A los 7 días se capturan solos los de después.</p>
            <Button size="sm" onClick={submit} disabled={saving} data-testid={`opt-form-submit-${campaign.id}`}>
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Registrar optimización
            </Button>
          </div>
        </div>
      )}

      {entries.length === 0 ? (
        <p className="text-xs text-muted-foreground italic" data-testid={`opt-empty-${campaign.id}`}>Sin optimizaciones registradas en esta campaña.</p>
      ) : (
        <div className="space-y-2">
          {entries.map((e) => <Entry key={e.id} clientId={clientId} entry={e} onChanged={onChanged} />)}
        </div>
      )}
    </div>
  );
}
